export default {
    title: "Document Management",
    subtitle: "Configure document types, schemas, attributes and training for the AI agent",
    tabs: {
        types: "Document Types",
        connections: "Doc Connection Manager",
        training: "Training",
    },
    types: {
        title: "Document Types",
        description: "Manage document types available in the system",
        newDocumentType: "New Document Type",
        filters: {
            keywordPlaceholder: "Search by keyword...",
            attributePlaceholder: "Filter by specific attribute",
            allAttributes: "All attributes",
        },
        headers: {
            key: "Key",
            label: "Label",
            description: "Description",
            schemasCount: "Schemas Count",
            attributesCount: "Attributes Count",
            actions: "Actions",
        },
        modal: {
            new: "New Document Type",
            edit: "Edit Document Type",
            fields: {
                key: "Key",
                keyPlaceholder: "e.g.: bill_of_lading",
                label: "Label",
                labelPlaceholder: "e.g.: Bill of Lading",
                description: "Description",
                descriptionPlaceholder: "Document type description",
            },
        },
        actions: {
            edit: "Edit",
            delete: "Delete",
            viewSchemas: "Schemas",
            viewAttributes: "Attributes",
        },
    },
    schemas: {
        title: "Document Schemas",
        description: "Manage schemas for each document type with vertical variants",
        newSchema: "New Schema",
        searchPlaceholder: "Search by name, type...",
        documentTypePlaceholder: "Filter by document type",
        regionPlaceholder: "Filter by region",
        statusPlaceholder: "Filter by status",
        headers: {
            documentType: "Document Type",
            schemaName: "Schema Name",
            schemaCode: "Code",
            region: "Region",
            attributeCount: "Attributes",
            parserRulesCount: "Parser Rules",
            isDefault: "Default",
            isActive: "Status",
            actions: "Actions",
        },
        modal: {
            new: "New Schema",
            edit: "Edit Schema",
            fields: {
                documentType: "Document Type",
                documentTypePlaceholder: "Select document type",
                schemaName: "Schema Name",
                schemaNamePlaceholder: "e.g.: WayBill China/Asia",
                schemaCode: "Schema Code",
                schemaCodePlaceholder: "e.g.: waybill_china_asia",
                version: "Version",
                versionPlaceholder: "e.g.: 1.0",
                description: "Description",
                descriptionPlaceholder: "Schema description",
                useCases: "Use Cases",
                useCasesPlaceholder: "When to use this schema",
                region: "Region",
                regionPlaceholder: "e.g.: asia, europe",
                countryCodes: "Country Codes",
                countryCodesPlaceholder: "e.g.: CN, JP, KR",
                carrierCodes: "Carrier Codes",
                carrierCodesPlaceholder: "e.g.: MAEU, MSC",
                isDefault: "Default Schema",
                isActive: "Active",
            },
        },
        actions: {
            edit: "Edit",
            delete: "Delete",
            viewAttributes: "Attributes",
            parserRules: "Parser Rules",
        },
        status: {
            active: "Active",
            inactive: "Inactive",
            default: "Default",
        },
    },
    schemaAttributes: {
        title: "Schema Attributes",
        description: "Manage attributes for this schema",
        backToSchemas: "Back to Schemas",
        newAttribute: "Add Attribute",
        searchPlaceholder: "Search attribute...",
        headers: {
            attributeKey: "Key",
            label: "Label",
            type: "Type",
            importance: "Importance",
            required: "Required",
            enumValues: "Enum Values",
            metadata: "Metadata",
            actions: "Actions",
        },
        modal: {
            new: "Add Attribute",
            edit: "Edit Attribute",
            createNewAttribute: "Create New Attribute",
            useExistingAttribute: "Use Existing Attribute",
            fields: {
                attribute: "Attribute",
                attributePlaceholder: "Select existing attribute",
                importanceLevel: "Importance Level",
                importanceLevelPlaceholder: "Select level",
                importanceLevels: {
                    error: "Error if missing",
                    warning: "Warning if missing",
                    optional: "Optional",
                },
                isRequired: "Required",
                displayOrder: "Display Order",
                customLabel: "Custom Label",
                customLabelPlaceholder: "Override label (optional)",
                customHelpText: "Custom Help Text",
                customHelpTextPlaceholder: "Override help text (optional)",
            },
        },
        importance: {
            error: "Error",
            warning: "Warning",
            optional: "Optional",
        },
        actions: {
            edit: "Edit",
            remove: "Remove",
        },
    },
    attributes: {
        title: "Global Attributes",
        description: "Manage reusable attributes for all schemas",
        newAttribute: "New Attribute",
        searchPlaceholder: "Search by key, label...",
        dataTypePlaceholder: "Filter by data type",
        categoryPlaceholder: "Filter by category",
        globalPlaceholder: "Filter by global",
        headers: {
            attributeKey: "Key",
            label: "Label",
            dataType: "Data Type",
            category: "Category",
            isGlobal: "Global",
            usageCount: "Usage",
            actions: "Actions",
        },
        modal: {
            new: "New Attribute",
            edit: "Edit Attribute",
            fields: {
                attributeKey: "Attribute Key",
                attributeKeyPlaceholder: "e.g.: shipper_name",
                label: "Label",
                labelPlaceholder: "e.g.: Shipper Name",
                description: "Description",
                descriptionPlaceholder: "Attribute description",
                dataType: "Data Type",
                dataTypePlaceholder: "Select data type",
                enumValues: "Enum Values",
                enumValuesPlaceholder: "Add values (one per line)",
                enumValuesHelp: "Only for enum type",
                metadata: "Metadata",
                metadataPlaceholder: '{"key": "value"}',
                metadataHelp: "Only for json type - Valid JSON",
                validationRules: "Validation Rules",
                validationRulesPlaceholder: '{"min": 0, "max": 100}',
                inputType: "Input Type",
                inputTypePlaceholder: "e.g.: text, number, date",
                placeholder: "Placeholder",
                placeholderPlaceholder: "Placeholder text",
                helpText: "Help Text",
                helpTextPlaceholder: "Help text for the user",
                category: "Category",
                categoryPlaceholder: "e.g.: parties, cargo, dates",
                displayOrder: "Display Order",
                isGlobal: "Global",
            },
        },
        actions: {
            edit: "Edit",
            delete: "Delete",
        },
        dataTypes: {
            string: "String",
            number: "Number",
            date: "Date",
            datetime: "Date/Time",
            boolean: "Boolean",
            array: "Array",
            object: "Object",
            email: "Email",
            url: "URL",
            phone: "Phone",
        },
    },
    connections: {
        title: "Doc Connection Manager",
        description: "Link attributes from different document type schemas to facilitate cross-document validation",
        newConnection: "New Connection",
        searchPlaceholder: "Search connection...",
        headers: {
            source: "Source",
            target: "Target",
            validationRule: "Validation Rule",
            created: "Created",
            actions: "Actions",
        },
        modal: {
            new: "New Connection",
            edit: "Edit Connection",
            source: "Source",
            target: "Target",
            fields: {
                sourceDocumentType: "Source Document Type",
                sourceSchema: "Source Schema",
                sourceAttribute: "Source Attribute",
                targetDocumentType: "Target Document Type",
                targetSchema: "Target Schema",
                targetAttribute: "Target Attribute",
                validationRule: "Validation Rule",
            },
        },
        validationRules: {
            mustMatch: "Must Match",
            mustBeDifferent: "Must Be Different",
            sourceContainsTarget: "Source Contains Target",
            targetContainsSource: "Target Contains Source",
            mustReference: "Must Reference",
            mustBeSimilar: "Must Be Similar",
        },
        actions: {
            edit: "Edit",
            delete: "Delete",
        },
    },
    training: {
        title: "Training Documents",
        description: "Upload documents for AI agent training",
        uploadDocuments: "Upload Documents",
        uploadArea: "Drag files here or click to select",
        supportedFormats: "Supported formats: PDF, PNG, JPG",
        searchPlaceholder: "Search document...",
        documentTypePlaceholder: "Filter by type",
        statusPlaceholder: "Filter by status",
        headers: {
            fileName: "File Name",
            documentType: "Type",
            status: "Status",
            uploadDate: "Upload Date",
            extractionScore: "Extraction Score",
            validationScore: "Validation Score",
            detectedSchema: "Detected Schema",
            actions: "Actions",
        },
        status: {
            pending: "Pending",
            processing: "Processing",
            completed: "Completed",
            failed: "Failed",
        },
        validationStatus: {
            correct: "Correct",
            incorrect: "Incorrect",
            pending: "To Validate",
        },
        actions: {
            viewDetails: "Details",
            delete: "Delete",
            correct: "Correct",
            incorrect: "Incorrect",
            loading: "Loading...",
        },
        breadcrumb: {
            trainingDocuments: "Training Documents",
        },
        detail: {
            title: "Training Process Details",
            backToList: "Back to List",
            documentTitle: "Document",
            extractedDataTitle: "Extracted Data by Provider",
            infoTitle: "Process and Training Info",
            notFound: {
                title: "Document not found",
                message: "The requested training document does not exist.",
                backToList: "Back to List",
            },
            infoFields: {
                documentType: "Document Type",
                status: "Status",
                dataset: "Dataset",
                datasetVersion: "Dataset Version",
                schema: "Schema",
                detectedSchema: "Detected Schema",
                extractionScore: "Extraction Score",
                validationScore: "Validation Score",
                createdAt: "Creation Date",
                updatedAt: "Last Update",
                aiProviders: "AI Providers Used",
                customVariables: "Custom Variables",
                notes: "Notes",
            },
            tableHeaders: {
                attribute: "Attribute",
                extractedValue: "Extracted Value",
                confidence: "Confidence",
                validationStatus: "Validation Status",
                actions: "Actions",
            },
        },
        modal: {
            details: "Processing Details",
            fields: {
                fileName: "File Name",
                documentType: "Document Type",
                datasetName: "Dataset Name",
                datasetVersion: "Dataset Version",
                status: "Status",
                extractionScore: "Extraction Score",
                validationScore: "Validation Score",
                detectedSchema: "Detected Schema",
                processingTime: "Processing Time",
                extractedFields: "Extracted Fields",
                validationIssues: "Validation Issues",
            },
        },
        upload: {
            success: "Documents uploaded successfully",
            error: "Upload error",
            processing: "Processing...",
        },
    },
    parserRules: {
        title: "Parser Rules",
        description: "Manage Natural Language rules for the Parser agent",
        status: {
            active: "Active",
            inactive: "Inactive",
        },
        priority: "Priority",
        actions: {
            add: "Add Rule",
            edit: "Edit",
            delete: "Delete",
            cancelEdit: "Cancel Edit",
        },
        modal: {
            title: "Parser Rules",
            existingRules: "Existing Rules",
            noRules: "No rules defined for this schema",
            addRule: "Add New Rule",
            editRule: "Edit Rule",
            fields: {
                ruleName: "Rule Name",
                ruleNamePlaceholder: "e.g.: Container Code Validation",
                naturalLanguageRule: "Natural Language Rule",
                naturalLanguageRulePlaceholder: "Write the rule in natural language that the Parser agent should follow...",
                naturalLanguageRuleHelp: "Describe in natural language how the agent should treat the document once classified with this schema",
                description: "Description",
                descriptionPlaceholder: "Optional rule description",
                priority: "Priority",
                isActive: "Active",
            },
        },
    },
    modal: {
        cancel: "Cancel",
        save: "Save",
        create: "Create",
        delete: "Delete",
        close: "Close",
        confirmDelete: "Confirm Deletion",
        confirmDeleteMessage: "Are you sure you want to delete this item? This action cannot be undone.",
    },
    loading: "Loading...",
    noData: {
        title: "No data available",
        message: "There are no items to display",
        createFirst: "Create the first item",
    },
    breadcrumb: {
        documentTypes: "Document Types",
        schemas: "Schemas",
        attributes: "Attributes",
    },
    common: {
        yes: "Yes",
        no: "No",
        selectFile: "Select File",
        selectedFiles: "Selected files:",
        upload: "Upload",
        selectAttribute: "Select an attribute",
        selectAtLeastOneFile: "Select at least one file",
        insertAliasPerLine: "Insert one alias per line",
        invalidJson: "Invalid JSON",
        global: "Global",
        specific: "Specific",
    },
};
